import { useEffect, useRef, useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { BaseInput, RadioButtons } from "@smart/itops-sb-design-system-dom";
import { isNullOrUndefined } from "@smart/itops-utils-basic";

import {
  DynamicOptionsPlaceholder,
  shouldShowDynamicOptionsPlaceholder,
} from "./dynamic-options-placeholder";
import { FieldGroup } from "./field-group";
import { FieldSet } from "./input";
import { optionsWithFallback } from "./item";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";

export const RadioField = ({
  field,
  index,
  dir,
  innerRef,
  value,
  error,
  loading,
  disabled,
  onChange,
  onBlur,
  isPreview,
}: FieldProps<HTMLInputElement> & { dir?: "column" | "row" }) => {
  const { dynamicOptionList } = useFeatureFlags();
  const firstItemRef = useRef<HTMLInputElement>(null);
  const otherRef = useRef<HTMLInputElement>(null);
  const [other, setOther] = useState(
    () => !!value && !field.options?.find((o) => o.value === value),
  );
  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  useEffect(() => {
    if (isNullOrUndefined(value)) setOther(false);
  }, [value]);

  const otherOption = { value: "other", label: "Other" };
  const radioOptions = !field.allowCustomResponse
    ? optionsWithFallback(field.options)
    : [...optionsWithFallback(field.options), otherOption];

  if (dynamicOptionList && shouldShowDynamicOptionsPlaceholder(field)) {
    return (
      <DynamicOptionsPlaceholder
        field={field}
        index={index}
        error={error}
        loading={loading}
        disabled={disabled}
        isPreview={isPreview}
      />
    );
  }

  return (
    <FieldWrapper disabled={disabled} isLoading={loading} className="text-ui">
      <FieldGroup
        id={id}
        hint={field.hint ?? undefined}
        label={<Label {...field} />}
      >
        <FieldSet
          role="radiogroup"
          uri={field.uri}
          error={error}
          errorId={errorId}
          direction={dir}
        >
          <input
            ref={innerRef}
            id={id}
            name={id}
            value={value ?? ""}
            className="hidden"
            aria-invalid={!!error}
            aria-errormessage={error ? errorId : undefined}
            readOnly
            onFocus={() => {
              if (other) {
                otherRef.current?.focus();
              } else {
                firstItemRef.current?.focus();
              }
            }}
          />
          <RadioButtons
            ref={firstItemRef}
            options={radioOptions}
            value={other ? otherOption.value : value}
            withLabelWrapper={false}
            onChange={(v) => {
              if (v === otherOption.value) {
                setOther(true);
                onChange("");
                onBlur();
                otherRef.current?.focus();
              } else {
                setOther(false);
                onChange(v);
                onBlur();
              }
            }}
            onClearClick={() => {
              onChange(null);
              onBlur();
            }}
          />
          {field.allowCustomResponse && other && (
            <BaseInput
              ref={otherRef}
              name={fieldName({ field, index, suffix: "other-input" })}
              type="text"
              autoFocus
              value={other ? value ?? "" : ""}
              aria-hidden={!other}
              aria-label="Other Value"
              className="ml-[2.8rem]"
              disabled={disabled}
              onChange={(e) => onChange(e.currentTarget.value)}
              onBlur={onBlur}
            />
          )}
        </FieldSet>
      </FieldGroup>
    </FieldWrapper>
  );
};
